import type { ReactNode } from 'react'
import { cx } from './cx'

/** Accent-bordered cards exist for warnings and destructive zones. */
export type CardTone = 'default' | 'warning' | 'danger'

const TONE: Record<CardTone, string> = {
  default: 'bg-white border-gray-200',
  warning: 'bg-white border-amber-200',
  danger: 'bg-white border-red-200',
}

// `tinted` fills the surface too. Kept as a separate map rather than a class
// appended by the caller: Tailwind decides bg-white vs bg-amber-50 by emit
// order, not by the order they appear in a className string, so overriding a
// Card's fill from outside silently loses.
const TINTED: Record<CardTone, string> = {
  default: 'bg-gray-50 border-gray-200',
  warning: 'bg-amber-50 border-amber-200',
  danger: 'bg-red-50 border-red-200',
}

export default function Card({
  tone = 'default',
  tinted = false,
  padded = true,
  className,
  children,
}: {
  tone?: CardTone
  /** Fill the surface with the tone's tint instead of leaving it white. */
  tinted?: boolean
  /** Off when the card owns a divided list and the rows carry their own padding. */
  padded?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cx('rounded-xl border', (tinted ? TINTED : TONE)[tone], padded && 'p-5', className)}>
      {children}
    </div>
  )
}

/** Title row for a card. `actions` sits on the right and wraps on narrow screens. */
export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('flex items-start justify-between gap-3 flex-wrap', className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}
